import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const CartContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const CartProvider = ({ children }) => {
    const { token, isApproved } = useAuth();
    const [cart, setCart] = useState({ items: [], total_ht: 0, total_vat: 0, total_ttc: 0, reservation_expires: null, reservation_unlimited: false, has_negotiated_discount: false });
    const [loading, setLoading] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState(null);
    const timerRef = useRef(null);

    const fetchCart = useCallback(async () => {
        if (!token || !isApproved) return;
        
        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}/cart`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            // Check if any items were removed due to ERP status change
            if (response.data.removed_items && response.data.removed_items.length > 0) {
                const removedCount = response.data.removed_items.length;
                const removedNames = response.data.removed_items
                    .map(item => `${item.brand} ${item.model}`)
                    .join(', ');
                toast.error(
                    `${removedCount} article(s) retiré(s) du panier (vendu(s) ou réservé(s) ailleurs): ${removedNames}`,
                    { duration: 8000 }
                );
            }
            
            setCart(response.data);
        } catch (error) {
            console.error('Fetch cart error:', error);
        } finally {
            setLoading(false);
        }
    }, [token, isApproved]);

    useEffect(() => {
        fetchCart();
    }, [fetchCart]);

    // Auto-refresh cart every 30 seconds to detect ERP status changes
    useEffect(() => {
        if (!token || !isApproved) return;
        
        const refreshInterval = setInterval(() => {
            fetchCart();
        }, 30000); // 30 seconds
        
        return () => clearInterval(refreshInterval);
    }, [token, isApproved, fetchCart]);

    // Timer logic
    useEffect(() => {
        if (cart.reservation_expires) {
            const updateTimer = () => {
                const now = new Date();
                const expires = new Date(cart.reservation_expires);
                const diff = expires - now;
                
                if (diff <= 0) {
                    setTimeRemaining(null);
                    fetchCart(); // Refresh cart when timer expires
                } else {
                    setTimeRemaining(Math.floor(diff / 1000));
                }
            };

            updateTimer();
            timerRef.current = setInterval(updateTimer, 1000);

            return () => {
                if (timerRef.current) {
                    clearInterval(timerRef.current);
                }
            };
        } else {
            setTimeRemaining(null);
        }
    }, [cart.reservation_expires, fetchCart]);

    const addToCart = async (productId) => {
        try {
            await axios.post(`${API_URL}/cart/add`, 
                { product_id: productId },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            await fetchCart();
            return true;
        } catch (error) {
            throw error;
        }
    };

    const removeFromCart = async (productId) => {
        try {
            await axios.delete(`${API_URL}/cart/remove/${productId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            await fetchCart();
        } catch (error) {
            console.error('Remove from cart error:', error);
            throw error;
        }
    };

    const extendReservation = async () => {
        try {
            await axios.post(`${API_URL}/cart/extend`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            await fetchCart();
        } catch (error) {
            console.error('Extend reservation error:', error);
            throw error;
        }
    };

    const verifyAvailability = async () => {
        try {
            const response = await axios.post(`${API_URL}/cart/verify-availability`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error('Verify availability error:', error);
            throw error;
        }
    };

    const clearCart = () => {
        setCart({ items: [], total_ht: 0, total_vat: 0, total_ttc: 0, reservation_expires: null, reservation_unlimited: false, has_negotiated_discount: false });
    };

    const formatTime = (seconds) => {
        if (!seconds || seconds < 0) return '00:00';
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const value = {
        cart,
        loading,
        addToCart,
        removeFromCart,
        extendReservation,
        verifyAvailability,
        fetchCart,
        clearCart,
        timeRemaining,
        formattedTime: cart.reservation_unlimited ? 'Illimité' : formatTime(timeRemaining),
        hasItems: cart.items.length > 0,
        itemCount: cart.items.length,
        isExpiringSoon: !cart.reservation_unlimited && timeRemaining !== null && timeRemaining < 120, // Less than 2 minutes
        isUnlimitedReservation: cart.reservation_unlimited || false,
        hasNegotiatedDiscount: cart.has_negotiated_discount || false
    };

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};
